import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Link } from 'react-router-dom';
import Sidebar from '../Navigation/Sidebar/Sidebar';
import { ArrowBack } from '@mui/icons-material';
import { FiTrash2, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import InactivateEmailDialog from './popup';

const days = ['All Day', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const statuses = ['All', 'Active This Week', 'Active', 'Inactive'];

const AgentRoster = ({ 
  rawAgentData, 
  update_roster, 
  open, 
  setOpen, 
  deleteModal,
  onDeleteClick,
  onBulkDeleteClick,
  onConfirmDelete,
  onCloseDeleteModal,
  successPopup,
  onCloseSuccessPopup,
  loading,
  error,
  selectedRows,
  onRowSelection,
  onSelectAll,
  statusFilter = 'All',
  onStatusFilterChange,
  onRefresh,
  setError
}) => {
  const [search, setSearch] = useState('');
  const [dayFilter, setDayFilter] = useState('All Day');
  const [mylist, setMylist] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (err) {
      return dateString;
    }
  }, []);

  const flattenedData = useMemo(() => {
    return rawAgentData?.flatMap((agent) =>
      agent.agent_Time?.map((timeSlot) => ({
        id: timeSlot.id,
        email: agent.agent_Email,
        startDate: formatDate(timeSlot.start_date),
        day: timeSlot.day,
        time: `${timeSlot.start_Time.slice(0, 5)} - ${timeSlot.end_Time.slice(0, 5)}`,
        startTime: timeSlot.start_Time.slice(0, 5),
        endTime: timeSlot.end_Time.slice(0, 5),
        status: timeSlot.isActive ? 'Active' : 'Inactive',
      }))
    ) || [];
  }, [rawAgentData, formatDate]);

  const filteredData = useMemo(() => {
    return flattenedData?.filter((entry) => {
      const matchesSearch = entry.email.toLowerCase().includes(search.toLowerCase());
      const matchesDay = dayFilter === 'All Day' || entry.day === dayFilter;
      
      let matchesStatus = true;
      if (statusFilter === 'All') {
        matchesStatus = true;
      } else if (statusFilter === 'Active') {
        matchesStatus = entry.status === 'Active';
      } else if (statusFilter === 'Inactive') {
        matchesStatus = entry.status === 'Inactive';
      } else if (statusFilter === 'Active This Week') {
        matchesStatus = entry.status === 'Active';
      }
      
      return matchesSearch && matchesDay && matchesStatus;
    });
  }, [flattenedData, search, dayFilter, statusFilter]);

  const isAllSelected = useMemo(() => {
    return filteredData.length > 0 && 
      filteredData.every(row => selectedRows?.some(selected => selected.id === row.id));
  }, [filteredData, selectedRows]);

  const handleSelectAllChange = useCallback((event) => {
    onSelectAll(event.target.checked, filteredData);
  }, [onSelectAll, filteredData]);

  const handleStatusFilterChange = useCallback((event) => {
    if (onStatusFilterChange) {
      onStatusFilterChange(event.target.value);
    }
  }, [onStatusFilterChange]);

  const handleUpdateClick = useCallback((e, entry) => {
    e.stopPropagation();
    setMylist(entry);
    setOpen(true);
  }, [setOpen]);

  const handleCloseUpdateModal = useCallback(() => {
    setOpen(false);
    setMylist(null);
  }, [setOpen]);

  const handleUpdateConfirm = useCallback((newStatus) => {
    if (mylist) {
      update_roster(null, mylist, newStatus);
    }
  }, [mylist, update_roster]);

  // Smooth Delete Confirmation Modal - No blink
  const DeleteConfirmationModal = useCallback(() => {
    if (!deleteModal?.show) return null;

    const isBulk = deleteModal.type === 'bulk';
    const itemCount = deleteModal.items.length;

    return (
      <Dialog
        open={deleteModal.show}
        onClose={onCloseDeleteModal}
        disableEscapeKeyDown
        TransitionProps={{ timeout: 0 }}
        PaperProps={{
          sx: {
            borderRadius: '1rem',
            padding: '1rem',
            maxWidth: '28rem',
            width: '90%',
            transition: 'none'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <FiAlertCircle size={48} style={{ color: '#f44336', marginBottom: '0.5rem' }} />
          <Typography variant="h6" component="div" fontWeight="bold">
            {isBulk ? `Delete ${itemCount} Schedule${itemCount > 1 ? 's' : ''}` : 'Delete Schedule'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center', mb: 2 }}>
            {isBulk 
              ? `Are you sure you want to delete ${itemCount} selected schedule${itemCount > 1 ? 's' : ''}?` 
              : 'Are you sure you want to delete this schedule?'
            }
          </DialogContentText>
          
          {!isBulk && deleteModal.items[0] && (
            <Box sx={{ 
              background: '#f5f5f5', 
              padding: '1rem', 
              borderRadius: '0.5rem',
              mb: 2
            }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Email:</strong> {deleteModal.items[0].email}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Start Date:</strong> {deleteModal.items[0].startDate}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Day:</strong> {deleteModal.items[0].day}
              </Typography>
              <Typography variant="body2">
                <strong>Time Slot:</strong> {deleteModal.items[0].time}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
          <Button
            onClick={onCloseDeleteModal}
            variant="outlined"
            sx={{
              borderRadius: '2rem',
              padding: '0.5rem 1.5rem',
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirmDelete}
            variant="contained"
            color="error"
            sx={{
              borderRadius: '2rem',
              padding: '0.5rem 1.5rem',
              textTransform: 'none',
              position: 'relative'
            }}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ position: 'absolute' }} />
            ) : (
              isBulk ? `Delete ${itemCount} Schedule${itemCount > 1 ? 's' : ''}` : 'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [deleteModal, onCloseDeleteModal, onConfirmDelete, loading]);

  const SuccessPopupModal = useCallback(() => {
    if (!successPopup?.show) return null;

    return (
      <Dialog
        open={successPopup.show}
        onClose={onCloseSuccessPopup}
        TransitionProps={{ timeout: 0 }}
        PaperProps={{
          sx: {
            borderRadius: '1rem',
            padding: '1rem',
            maxWidth: '24rem',
            width: '90%',
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <FiCheckCircle size={48} style={{ color: '#4caf50', marginBottom: '0.5rem' }} />
          <Typography variant="h6" component="div" fontWeight="bold">
            Success!
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center' }}>
            {successPopup.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={onCloseSuccessPopup}
            variant="contained"
            sx={{
              borderRadius: '2rem',
              padding: '0.5rem 2rem',
              textTransform: 'none',
              backgroundColor: '#4caf50',
              '&:hover': { backgroundColor: '#45a049' }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [successPopup, onCloseSuccessPopup]);

  const ErrorPopupModal = useCallback(() => {
    if (!error) return null;

    return (
      <Dialog
        open={!!error}
        onClose={() => setError && setError(null)}
        TransitionProps={{ timeout: 0 }}
        PaperProps={{
          sx: {
            borderRadius: '1rem',
            padding: '1rem',
            maxWidth: '24rem',
            width: '90%',
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <FiAlertCircle size={48} style={{ color: '#f44336', marginBottom: '0.5rem' }} />
          <Typography variant="h6" component="div" fontWeight="bold">
            Error
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center' }}>
            {error}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={() => setError && setError(null)}
            variant="contained"
            color="error"
            sx={{
              borderRadius: '2rem',
              padding: '0.5rem 2rem',
              textTransform: 'none'
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [error, setError]);

  return (
    <>
      {open && (
        <InactivateEmailDialog 
          onConfirm={handleUpdateConfirm} 
          email={mylist?.email} 
          update_roster={update_roster} 
          mylist={mylist} 
          handleClose={handleCloseUpdateModal} 
        />
      )}
      
      <DeleteConfirmationModal />
      <SuccessPopupModal />
      <ErrorPopupModal />
      
      <div className="main-div">
        <div>
          <Sidebar />
        </div>
        <Box p={4}>
          <Typography variant="h6" fontWeight="bold" mb={2} display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Link to="/">
                <ArrowBack sx={{ color: 'black', mr: 1 }} />
              </Link>
              Customer Support Agent Rooster
            </Box>
          </Typography>

          <Box display="flex" justifyContent="space-between" mb={2} flexWrap="wrap" gap={2}>
            <TextField
              placeholder="Search by agent email"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />

            <Select
              size="small"
              value={dayFilter}
              onChange={(e) => setDayFilter(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              {days.map((day) => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </Select>

            <Select
              size="small"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              sx={{ minWidth: 180 }}
            >
              {statuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>

            <Link to="/create_cst_rooster">
              <Button variant="contained" sx={{ backgroundColor: '#14AE78', '&:hover': { backgroundColor: '#0e9c68' } }}>
                + Create
              </Button>
            </Link>
          </Box>

          {statusFilter === 'Active This Week' && (
            <Box 
              sx={{ 
                backgroundColor: '#e3f2fd', 
                padding: '0.75rem 1rem', 
                borderRadius: '0.5rem',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <FiCheckCircle color="#1976d2" size={18} />
              <Typography variant="body2" color="#1976d2">
                Showing only current week's active schedules
              </Typography>
            </Box>
          )}

          <Box borderRadius={2} boxShadow={2} style={{ height: "70vh", overflow: "auto" }}>
            <Table stickyHeader>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell style={{ textAlign: 'center', fontWeight: 'bold', width: '50px' }}>
                    <Checkbox
                      checked={isAllSelected}
                      onChange={handleSelectAllChange}
                      indeterminate={selectedRows?.length > 0 && selectedRows.length < filteredData.length}
                    />
                  </TableCell>
                  <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>S.No.</TableCell>
                  <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>Email ID</TableCell>
                  <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>Start Date</TableCell>
                  <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>Day</TableCell>
                  <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>Time Slot</TableCell>
                  <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && !rawAgentData ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 1 }}>Loading...</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredData?.length > 0 ? (
                  filteredData.map((entry, index) => (
                    <TableRow key={entry.id || index} hover>
                      <TableCell align="center">
                        <Checkbox
                          checked={selectedRows?.some(row => row.id === entry.id)}
                          onChange={(e) => onRowSelection(entry, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{entry.email}</TableCell>
                      <TableCell align="center">{entry.startDate}</TableCell>
                      <TableCell align="center">{entry.day}</TableCell>
                      <TableCell align="center">{entry.time}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={entry.status}
                          color={entry.status === 'Active' ? 'success' : 'error'}
                          variant="outlined"
                          sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                          onClick={(e) => handleUpdateClick(e, entry)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <button
                          onClick={() => onDeleteClick(entry)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#f44336',
                            fontSize: '1.2rem',
                            padding: '0.5rem',
                            borderRadius: '0.25rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        {statusFilter === 'Active This Week' ? 'No active schedules found for current week' : 'No records found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          
          {selectedRows?.length > 0 && (
            <Box textAlign="center" mt={3}>
              <Button
                variant="contained"
                color="error"
                startIcon={<FiTrash2 />}
                onClick={onBulkDeleteClick}
                sx={{
                  borderRadius: '2rem',
                  textTransform: 'none',
                  backgroundColor: '#f44336',
                  '&:hover': { backgroundColor: '#d32f2f' },
                  px: 3
                }}
              >
                Delete Selected ({selectedRows.length})
              </Button>
            </Box>
          )}
        </Box>
      </div>
    </>
  );
};

export default React.memo(AgentRoster);